import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCcw, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, Package, AlertTriangle, CheckCircle2,
  TrendingUp, Filter, Calendar, Zap
} from "lucide-react";
import apinode from "../../services/apinode";

const dtBR = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "");

// 🔹 tradução + tipos
const translateStatus = (status) => {
  if (!status) return "-";
  const map = {
    opened: "Aberto", open: "Aberto", closed: "Fechado", paid: "Pago",
    cancelled: "Cancelado", partially_refunded: "Parcial Reembolsado",
    in_progress: "Em andamento", mediations: "Mediação", returns: "Devolução",
    dispute: "Disputa", none: "—", claim: "Reclamação", return: "Devolução",
    refund: "Reembolso", shipped: "Enviado", delivered: "Entregue",
    accepted: "Aceito", rejected: "Rejeitado", seller: "Vendedor",
    buyer: "Comprador", system: "Sistema"
  };
  return map[status] || status;
};

const getMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  return { start, end };
};

export default function ClaimsAdmin() {
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(false);

  const [apelido, setApelido] = useState("");
  const [status, setStatus] = useState("");
  const { start: defaultStart, end: defaultEnd } = getMonthRange();
  const [dateStart, setDateStart] = useState(defaultStart.toISOString().slice(0, 10));
  const [dateEnd, setDateEnd] = useState(defaultEnd.toISOString().slice(0, 10));

  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [openRow, setOpenRow] = useState(null);

  const [details, setDetails] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apinode.get(`/meli/claims/list/${localStorage.idconta}`);
      setRaw(r?.data?.data || []);
      setPage(1);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchDetail = async (claimId, conta) => {
    try {
      setLoadingDetail(claimId);
      const res = await apinode.get(`/meli/claims/${claimId}/detail/${conta}`);
      setDetails(prev => ({ ...prev, [claimId]: res.data }));
    } catch (err) {
      console.error("Erro ao buscar detalhe da claim:", err);
    } finally {
      setLoadingDetail(null);
    }
  };

  const filteredByDate = useMemo(() => {
    const start = new Date(dateStart);
    const end = new Date(dateEnd);
    return raw.filter(r => {
      const d = new Date(r.date_created);
      return d >= start && d <= end;
    });
  }, [raw, dateStart, dateEnd]);

  const filtered = useMemo(() => {
    return filteredByDate.filter(r =>
      (apelido ? r.apelido === apelido : true) &&
      (status ? r.status === status : true)
    );
  }, [filteredByDate, apelido, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const startIdx = (safePage - 1) * pageSize;
    return filtered.slice(startIdx, startIdx + pageSize);
  }, [filtered, safePage, pageSize]);

  const stats = {
    total: filtered.length,
    abertos: filtered.filter(r => ["open", "opened", "in_progress"].includes(r.status)).length,
    fechados: filtered.filter(r => r.status === "closed").length,
    semana: filtered.filter(r => new Date(r.date_created) >= new Date(Date.now() - 7 * 86400000)).length
  };

  const uniqueApelidos = [...new Set(filteredByDate.map(r => r.apelido).filter(Boolean))];
  const uniqueStatus = [...new Set(filteredByDate.map(r => r.status).filter(Boolean))];

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    for (let i = Math.max(2, safePage - delta); i <= Math.min(totalPages - 1, safePage + delta); i++) {
      range.push(i);
    }
    if (safePage - delta > 2) range.unshift("...");
    if (safePage + delta < totalPages - 1) range.push("...");
    range.unshift(1);
    if (totalPages > 1) range.push(totalPages);
    return range;
  };

  return (
    <div className="root">
      <style jsx>{`
        .root { font-family: 'Inter', sans-serif; background:#f9fafb; min-height:100vh; }
        .nav { background:#fff; border-bottom:1px solid #eee; padding:1rem 2rem; display:flex; justify-content:space-between; align-items:center; position:sticky; top:0; z-index:50; }
        .btn { border:none; border-radius:8px; padding:0.5rem 1rem; cursor:pointer; display:flex; align-items:center; gap:0.5rem; font-weight:500; background:linear-gradient(135deg,#2563eb,#3b82f6); color:#fff; }
        .btn:hover{filter:brightness(1.1);}
        .stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:1rem;padding:1.5rem 2rem;}
        .card{background:#fff;border-radius:12px;padding:1.25rem 1.5rem;box-shadow:0 4px 10px rgba(0,0,0,0.05);display:flex;flex-direction:column;gap:0.5rem;}
        .card h4{font-size:0.9rem;color:#6b7280;font-weight:600;margin:0;}
        .value{font-size:1.8rem;font-weight:700;color:#111;}
        .filters{background:#fff;margin:0 2rem 1rem;border-radius:12px;padding:1rem 1.5rem;box-shadow:0 4px 12px rgba(0,0,0,0.05);display:flex;gap:1rem;align-items:center;flex-wrap:wrap;}
        select,input{padding:0.5rem;border:1px solid #ddd;border-radius:8px;min-width:150px;}
        .table-container{background:#fff;margin:1rem 2rem;border-radius:12px;overflow:hidden;box-shadow:0 4px 12px rgba(0,0,0,0.05);}
        table{width:100%;border-collapse:collapse;}
        th{background:#f3f4f6;padding:0.75rem 1rem;text-align:left;font-size:0.85rem;color:#374151;font-weight:600;}
        td{padding:0.75rem 1rem;border-top:1px solid #f0f0f0;font-size:0.9rem;}
        tr:nth-child(even) td{background:#fafafa;}
        tr:hover td{background:#fef9c3;}
        .expand{border:1px solid #d1d5db;background:#fff;border-radius:6px;padding:0.35rem;cursor:pointer;transition:all 0.2s;}
        .expand:hover{background:#e0e7ff;border-color:#2563eb;color:#2563eb;}
        .details{background:#f9fafb;padding:1rem;display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:1rem;}
        .kv{display:flex;flex-direction:column;}
        .kv span:first-child{font-size:0.75rem;color:#6b7280;font-weight:600;}
        .kv span:last-child{font-weight:500;}
        .badge{padding:0.25rem 0.65rem;border-radius:12px;font-size:0.75rem;font-weight:600;}
        .badge.Pago{background:#d1fae5;color:#065f46;}
        .badge.Cancelado{background:#fee2e2;color:#991b1b;}
        .badge.Aberto{background:#fef3c7;color:#92400e;}
        .badge.Fechado{background:#dbeafe;color:#1e40af;}
        .badge["Parcial Reembolsado"]{background:#ede9fe;color:#5b21b6;}
        .badge["Em andamento"]{background:#ffedd5;color:#9a3412;}
        .badge.Devolução{background:#f0fdf4;color:#166534;}
        .badge.Reclamação{background:#fef2f2;color:#991b1b;}
        .badge.Mediação{background:#f0f9ff;color:#075985;}
        .badge.Reembolso{background:#fff7ed;color:#9a3412;}
        .pagination{display:flex;justify-content:center;align-items:center;gap:0.4rem;padding:1rem;background:#fff;border-top:1px solid #eee;}
        .page-btn{border:1px solid #d1d5db;background:#fff;min-width:38px;height:34px;border-radius:6px;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:0.85rem;transition:all 0.2s ease;color:#374151;font-weight:500;}
        .page-btn:hover:not(:disabled){background:#f3f4f6;}
        .page-btn.active{background:#2563eb;color:#fff !important;font-weight:700;border-color:#2563eb;}
        .page-btn:disabled{background:#f9fafb;color:#9ca3af;cursor:not-allowed;}
      `}</style>

      {/* NAV */}
      <div className="nav">
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 700 }}>
          <Package /> Claims Admin
        </div>
        <button className="btn" onClick={fetchData}>
          <RefreshCcw size={16} /> Atualizar
        </button>
      </div>

      {/* KPIs */}
      <div className="stats">
        <div className="card"><h4>Total no período</h4><div className="value">{stats.total}</div></div>
        <div className="card"><h4><AlertTriangle size={18} /> Abertos</h4><div className="value">{stats.abertos}</div></div>
        <div className="card"><h4><CheckCircle2 size={18} /> Fechados</h4><div className="value">{stats.fechados}</div></div>
        <div className="card"><h4><TrendingUp size={18} /> Últimos 7 dias</h4><div className="value">{stats.semana}</div></div>
      </div>

      {/* FILTERS */}
      <div className="filters">
        <Calendar size={18} style={{ color: "#6b7280" }} />
        <input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)} />
        <input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)} />
        <Filter size={18} style={{ color: "#6b7280" }} />
        <select value={apelido} onChange={e => setApelido(e.target.value)}>
          <option value="">Todos os apelidos</option>
          {uniqueApelidos.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Todos os status</option>
          {uniqueStatus.map(s => <option key={s} value={s}>{translateStatus(s)}</option>)}
        </select>
      </div>

      {/* TABLE */}
      <div className="table-container">
        {loading ? <p style={{ padding: "1rem" }}>Carregando...</p> : (
          <table>
            <thead>
              <tr>
                <th></th>
                <th>ID</th>
                <th>Pedido</th>
                <th>Cliente</th>
                <th>Apelido</th>
                <th>Situação</th>
                <th>Status</th>
                <th>Qtd</th>
                {/* <th>Fulfilled</th> */}
                <th>Tipo</th>
                <th>Criado</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(r => {
                const opened = openRow === r.id;
                const det = details[r.id];

                return (
                  <React.Fragment key={r.id}>
                    <tr>
                      <td>
                        <button
                          className="expand"
                          onClick={() => {
                            if (opened) {
                              setOpenRow(null);
                            } else {
                              setOpenRow(r.id);
                              if (!details[r.id]) {
                                fetchDetail(r.id, localStorage.idconta);
                              }
                            }
                          }}
                        >
                          {opened ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </td>
                      <td>{r.id}</td>
                      <td>{r.pedido}</td>
                      <td>{r.cliente}</td>
                      <td>{r.apelido}</td>
                      <td><span className={`badge ${translateStatus(r.situacao)}`}>{translateStatus(r.situacao)}</span></td>
                      <td><span className={`badge ${translateStatus(r.status)}`}>{translateStatus(r.status)}</span></td>
                      <td>{r.claimed_quantity}</td>
                      <td style={{ textAlign: "center" }}>
                        {/* {r.fulfilled ? <Zap size={18} color="#2563eb" title="Full" /> : <Zap size={18} color="#d1d5db" title="Parcial" />} */}
                      </td>
                      <td><span className={`badge ${translateStatus(r.return_type)}`}>{translateStatus(r.return_type)}</span></td>
                      <td>{dtBR(r.date_created)}</td>
                    </tr>

                    {opened && (
                      <tr>
                        <td colSpan={11}>
                          <div className="details">
                            <div className="kv"><span>Stage</span><span>{translateStatus(r.stage) || "-"}</span></div>
                            <div className="kv"><span>Tipo</span><span>{translateStatus(r.return_type) || "-"}</span></div>
                            <div className="kv"><span>Conferente</span><span>{r.conferente || "-"}</span></div>
                            <div className="kv"><span>Data Conf.</span><span>{dtBR(r.data_conferencia) || "-"}</span></div>
                            <div className="kv"><span>Resolution</span><span>{translateStatus(r.resolution_reason) || "-"}</span></div>
                            <div className="kv"><span>Atualizado</span><span>{dtBR(r.last_updated) || "-"}</span></div>
                            {loadingDetail === r.id && <p>Carregando detalhes...</p>}
                            {det ? (
                              <>
                                <div className="kv"><span>Título</span><span>{det.title || "-"}</span></div>
                                <div className="kv"><span>Descrição</span><span>{det.description || "-"}</span></div>
                                <div className="kv"><span>Problema</span><span>{det.problem || "-"}</span></div>
                                <div className="kv"><span>Responsável</span><span>{det.action_responsible || "-"}</span></div>
                                <div className="kv"><span>Prazo</span><span>{det.due_date ? dtBR(det.due_date) : "-"}</span></div>
                              </>
                            ) : (
                              !loadingDetail && <p>Não foi possível carregar os detalhes.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* PAGINATION */}
      <div className="pagination">
        <button className="page-btn" onClick={() => setPage(1)} disabled={safePage === 1}><ChevronsLeft size={16} /></button>
        <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}><ChevronLeft size={16} /></button>

        {getPageNumbers().map((num, i) => (
          num === "..." ? (
            <span key={i} style={{ padding: "0 0.5rem" }}>…</span>
          ) : (
            <button key={i} className={`page-btn ${safePage === num ? "active" : ""}`} onClick={() => setPage(num)}>
              {num}
            </button>
          )
        ))}

        <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}><ChevronRight size={16} /></button>
        <button className="page-btn" onClick={() => setPage(totalPages)} disabled={safePage === totalPages}><ChevronsRight size={16} /></button>
      </div>
    </div>
  );
}
